import { useState } from 'react'
import { Chip, iconSizes, Skeleton, Tooltip } from '@common/ui'
import { IconCloudCheck, IconCloudOff, IconEdit } from '@common/ui/icons'
import { com } from '@eidu/web'
import { PublishStatus } from './usePublishStatus'
import ResourceDiffDialog from './ResourceDiffDialog'
import EntityType = com.eidu.entity.type.EntityType

const shortVersionId = (versionId: string): string => versionId.split('-')[0] ?? versionId.slice(0, 8)

type PublishStatusChipProps = {
  status: PublishStatus
  entityType?: EntityType
  currentResourceKey: string | undefined
}

const PublishStatusChip = ({ status, entityType, currentResourceKey }: PublishStatusChipProps) => {
  const [diffOpen, setDiffOpen] = useState(false)

  if (status.state === 'loading') return <Skeleton radius="sm" width={96} height={24} />
  if (status.state === 'error') return null

  if (status.state === 'published')
    return (
      <Tooltip label={`Published version ${status.latest.versionId}`}>
        <Chip tone="positive" data-testid="publish-status-chip">
          <IconCloudCheck size={iconSizes.xs} stroke={1.5} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
          {`Published · ${shortVersionId(status.latest.versionId)}`}
        </Chip>
      </Tooltip>
    )

  if (status.reason === 'never-published')
    return (
      <Tooltip label="This asset has never been published.">
        <Chip tone="neutral" data-testid="publish-status-chip">
          <IconCloudOff size={iconSizes.xs} stroke={1.5} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
          Unpublished
        </Chip>
      </Tooltip>
    )

  const publishedPayloadKey = status.latest.payloadKey ?? undefined
  const canDiff =
    !!status.diff &&
    (status.diff.fieldChanges.length > 0 ||
      status.diff.linkChanges.length > 0 ||
      (!!publishedPayloadKey && !!currentResourceKey))

  return (
    <>
      <Tooltip
        label={canDiff ? 'View unpublished changes' : 'This asset has unpublished changes since the last version.'}
      >
        <Chip
          tone="caution"
          data-testid="publish-status-chip"
          onClick={canDiff ? () => setDiffOpen(true) : undefined}
          style={canDiff ? { cursor: 'pointer' } : undefined}
        >
          <IconEdit size={iconSizes.xs} stroke={1.5} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
          Draft
        </Chip>
      </Tooltip>
      {diffOpen && canDiff && (
        <ResourceDiffDialog
          onClose={() => setDiffOpen(false)}
          leftKey={publishedPayloadKey}
          rightKey={currentResourceKey}
          leftLabel={`Published · ${shortVersionId(status.latest.versionId)}`}
          rightLabel="Current draft"
          title="Unpublished changes"
          entityDiff={status.diff}
          entityType={entityType}
        />
      )}
    </>
  )
}

export default PublishStatusChip
